import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { placeholderTextMessages as messages } from '@atlaskit/editor-common/messages';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { bracketTyped, browser, hasDocAsParent, isEmptyDocument, isEmptyParagraph } from '@atlaskit/editor-common/utils';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { findParentNode } from '@atlaskit/editor-prosemirror/utils';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
// Typewriter animation timing constants
var TYPEWRITER_TYPE_DELAY = 50; // Delay between typing each character
var TYPEWRITER_PAUSE_BEFORE_ERASE = 2000; // Pause before starting to erase text
var TYPEWRITER_ERASE_DELAY = 40; // Delay between erasing each character
var TYPEWRITER_CYCLE_DELAY = 500; // Delay before starting next cycle
var TYPEWRITER_TYPED_AND_DELETED_DELAY = 1500; // Delay before starting animation after user typed and deleted

export var EMPTY_PARAGRAPH_TIMEOUT_DELAY = 2000; // Delay before showing placeholder on empty paragraph

export var pluginKey = new PluginKey('placeholderPlugin');
var placeholderTestId = 'placeholder-test-id';
function getPlaceholderState(editorState) {
  return pluginKey.getState(editorState);
}
var nodeTypesWithLongPlaceholderText = ['expand', 'panel'];
var nodeTypesWithShortPlaceholderText = ['tableCell', 'tableHeader'];
var nodeTypesWithSyncBlockPlaceholderText = ['bodiedSyncBlock'];
var cycleThroughPlaceholderPrompts = function cycleThroughPlaceholderPrompts(placeholderPrompts, activeTypewriterTimeouts, placeholderNodeWithText) {
  var initialDelayWhenUserTypedAndDeleted = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  var currentPromptIndex = 0;
  var displayedText = '';
  var animationTimeouts = [];
  var clearAllTimeouts = function clearAllTimeouts() {
    animationTimeouts.forEach(function (timeoutId) {
      return clearTimeout(timeoutId);
    });
    animationTimeouts = [];
  };
  var scheduleTimeout = function scheduleTimeout(callback, delay) {
    var timeoutId = setTimeout(callback, delay);
    animationTimeouts.push(timeoutId);
    return timeoutId;
  };
  var _startAnimationCycle = function startAnimationCycle() {
    var currentPrompt = placeholderPrompts[currentPromptIndex];
    var characterIndex = 0;
    var _typeNextCharacter = function typeNextCharacter() {
      if (characterIndex < currentPrompt.length) {
        displayedText = currentPrompt.substring(0, characterIndex + 1);
        placeholderNodeWithText.textContent = displayedText;
        characterIndex++;
        scheduleTimeout(_typeNextCharacter, TYPEWRITER_TYPE_DELAY);
      } else {
        scheduleTimeout(_eraseLastCharacter, TYPEWRITER_PAUSE_BEFORE_ERASE);
      }
    };
    var _eraseLastCharacter = function eraseLastCharacter() {
      if (displayedText.length > 1) {
        displayedText = displayedText.substring(0, displayedText.length - 1);
        placeholderNodeWithText.textContent = displayedText;
        scheduleTimeout(_eraseLastCharacter, TYPEWRITER_ERASE_DELAY);
      } else {
        displayedText = ' ';
        placeholderNodeWithText.textContent = displayedText;
        currentPromptIndex = (currentPromptIndex + 1) % placeholderPrompts.length;
        scheduleTimeout(_startAnimationCycle, TYPEWRITER_CYCLE_DELAY);
      }
    };
    _typeNextCharacter();
  };
  activeTypewriterTimeouts === null || activeTypewriterTimeouts === void 0 || activeTypewriterTimeouts.push(clearAllTimeouts);
  if (initialDelayWhenUserTypedAndDeleted > 0) {
    placeholderNodeWithText.textContent = ' ';
    scheduleTimeout(_startAnimationCycle, initialDelayWhenUserTypedAndDeleted);
  } else {
    _startAnimationCycle();
  }
};
export function createPlaceholderDecoration(editorState, placeholderText, placeholderPrompts, activeTypewriterTimeouts) {
  var pos = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 1;
  var initialDelayWhenUserTypedAndDeleted = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 0;
  var placeholderDecoration = document.createElement('span');
  var placeholderNodeWithText = placeholderDecoration;
  placeholderDecoration.setAttribute('data-testid', placeholderTestId);
  placeholderDecoration.className = 'placeholder-decoration';

  // PM sets contenteditable to false on Decorations so Firefox doesn't display the flashing cursor
  // So adding an extra span which will contain the placeholder text
  if (browser.gecko) {
    var placeholderNode = document.createElement('span');
    placeholderNode.setAttribute('contenteditable', 'true'); // explicitly overriding the default Decoration behaviour
    placeholderDecoration.appendChild(placeholderNode);
    placeholderNodeWithText = placeholderNode;
  }
  if (placeholderText) {
    placeholderNodeWithText.textContent = placeholderText || ' ';
  } else if (placeholderPrompts) {
    cycleThroughPlaceholderPrompts(placeholderPrompts, activeTypewriterTimeouts, placeholderNodeWithText, initialDelayWhenUserTypedAndDeleted);
  }

  // ME-2289 Tapping on backspace in empty editor hides and displays the keyboard
  // Add a editable buff node as the cursor moving forward is inevitable
  // when backspace in GBoard composition
  if (browser.android && browser.chrome) {
    var buffNode = document.createElement('span');
    buffNode.setAttribute('class', 'placeholder-android');
    buffNode.setAttribute('contenteditable', 'true');
    buffNode.textContent = ' ';
    placeholderDecoration.appendChild(buffNode);
  }
  var isTargetNested = editorState.doc.resolve(pos).depth > 1;

  // only truncate text for nested nodes, otherwise applying 'overflow: hidden;' to top level nodes
  // creates issues with quick insert button
  if (isTargetNested && editorExperiment('platform_editor_controls', 'variant1')) {
    placeholderDecoration.classList.add('placeholder-decoration-hide-overflow');
  }
  return DecorationSet.create(editorState.doc, [Decoration.widget(pos, placeholderDecoration, {
    side: 0,
    key: "placeholder ".concat(placeholderText)
  })]);
}
function setPlaceHolderState(_ref) {
  var placeholderText = _ref.placeholderText,
    pos = _ref.pos,
    placeholderPrompts = _ref.placeholderPrompts,
    typedAndDeleted = _ref.typedAndDeleted,
    userHadTyped = _ref.userHadTyped,
    canShowOnEmptyParagraph = _ref.canShowOnEmptyParagraph,
    showOnEmptyParagraph = _ref.showOnEmptyParagraph;
  return {
    hasPlaceholder: true,
    placeholderText: placeholderText,
    placeholderPrompts: placeholderPrompts,
    pos: pos ? pos : 1,
    typedAndDeleted: typedAndDeleted,
    userHadTyped: userHadTyped,
    canShowOnEmptyParagraph: canShowOnEmptyParagraph,
    showOnEmptyParagraph: showOnEmptyParagraph
  };
}
var emptyPlaceholder = function emptyPlaceholder(_ref2) {
  var placeholderText = _ref2.placeholderText,
    placeholderPrompts = _ref2.placeholderPrompts,
    userHadTyped = _ref2.userHadTyped,
    pos = _ref2.pos,
    canShowOnEmptyParagraph = _ref2.canShowOnEmptyParagraph,
    showOnEmptyParagraph = _ref2.showOnEmptyParagraph;
  return {
    hasPlaceholder: false,
    placeholderText: placeholderText,
    placeholderPrompts: placeholderPrompts,
    userHadTyped: userHadTyped,
    typedAndDeleted: false,
    canShowOnEmptyParagraph: canShowOnEmptyParagraph,
    showOnEmptyParagraph: showOnEmptyParagraph,
    pos: pos
  };
};
function createPlaceHolderStateFrom(_ref3) {
  var isInitial = _ref3.isInitial,
    isEditorFocused = _ref3.isEditorFocused,
    editorState = _ref3.editorState,
    isTypeAheadOpen = _ref3.isTypeAheadOpen,
    defaultPlaceholderText = _ref3.defaultPlaceholderText,
    intl = _ref3.intl,
    bracketPlaceholderText = _ref3.bracketPlaceholderText,
    emptyLinePlaceholder = _ref3.emptyLinePlaceholder,
    placeholderPrompts = _ref3.placeholderPrompts,
    typedAndDeleted = _ref3.typedAndDeleted,
    userHadTyped = _ref3.userHadTyped,
    isPlaceholderHidden = _ref3.isPlaceholderHidden,
    withEmptyParagraph = _ref3.withEmptyParagraph,
    showOnEmptyParagraph = _ref3.showOnEmptyParagraph;
  if (isPlaceholderHidden && fg('platform_editor_ai_aifc_patch_beta')) {
    return _objectSpread(_objectSpread({}, emptyPlaceholder({
      placeholderText: defaultPlaceholderText,
      placeholderPrompts: placeholderPrompts,
      userHadTyped: userHadTyped
    })), {}, {
      isPlaceholderHidden: isPlaceholderHidden
    });
  }
  if (isTypeAheadOpen !== null && isTypeAheadOpen !== void 0 && isTypeAheadOpen(editorState)) {
    return emptyPlaceholder({
      placeholderText: defaultPlaceholderText,
      placeholderPrompts: placeholderPrompts,
      userHadTyped: userHadTyped
    });
  }
  if ((defaultPlaceholderText || placeholderPrompts) && isEmptyDocument(editorState.doc)) {
    return setPlaceHolderState({
      placeholderText: defaultPlaceholderText,
      pos: 1,
      placeholderPrompts: placeholderPrompts,
      typedAndDeleted: typedAndDeleted,
      userHadTyped: userHadTyped
    });
  }
  if (fg('platform_editor_ai_aifc_patch_beta_2')) {
    var _editorState$selectio = editorState.selection,
      from = _editorState$selectio.from,
      to = _editorState$selectio.to,
      $to = _editorState$selectio.$to;
    if (defaultPlaceholderText && withEmptyParagraph && isEditorFocused && !isInitial && !isEmptyDocument(editorState.doc) && from === to && isEmptyParagraph($to.parent) && hasDocAsParent($to)) {
      return showOnEmptyParagraph ? setPlaceHolderState({
        placeholderText: defaultPlaceholderText,
        pos: to,
        placeholderPrompts: placeholderPrompts,
        typedAndDeleted: typedAndDeleted,
        userHadTyped: userHadTyped,
        canShowOnEmptyParagraph: true,
        showOnEmptyParagraph: true
      }) : emptyPlaceholder({
        placeholderText: defaultPlaceholderText,
        placeholderPrompts: placeholderPrompts,
        userHadTyped: userHadTyped,
        canShowOnEmptyParagraph: true,
        showOnEmptyParagraph: false,
        pos: to
      });
    }
  }
  if (isEditorFocused && editorExperiment('platform_editor_controls', 'variant1')) {
    var _parentNode$firstChil, _parentNode$firstChil2;
    var _editorState$selectio2 = editorState.selection,
      $from = _editorState$selectio2.$from,
      _$to = _editorState$selectio2.$to;
    if ($from.pos !== _$to.pos) {
      return emptyPlaceholder({
        placeholderText: defaultPlaceholderText,
        placeholderPrompts: placeholderPrompts,
        userHadTyped: userHadTyped
      });
    }
    var parentNode = $from.node($from.depth - 1);
    var parentType = parentNode === null || parentNode === void 0 ? void 0 : parentNode.type.name;
    if (emptyLinePlaceholder && parentType === 'doc') {
      var isEmptyLine = isEmptyParagraph($from.parent);
      if (isEmptyLine) {
        return setPlaceHolderState({
          placeholderText: emptyLinePlaceholder,
          pos: $from.pos,
          placeholderPrompts: placeholderPrompts,
          typedAndDeleted: typedAndDeleted,
          userHadTyped: userHadTyped
        });
      }
    }
    var isEmptyNode = (parentNode === null || parentNode === void 0 ? void 0 : parentNode.childCount) === 1 && ((_parentNode$firstChil = parentNode.firstChild) === null || _parentNode$firstChil === void 0 ? void 0 : _parentNode$firstChil.content.size) === 0 && ((_parentNode$firstChil2 = parentNode.firstChild) === null || _parentNode$firstChil2 === void 0 ? void 0 : _parentNode$firstChil2.type.name) === 'paragraph';
    if (nodeTypesWithShortPlaceholderText.includes(parentType) && isEmptyNode) {
      var _table$node$firstChil;
      var table = findParentNode(function (node) {
        return node.type === editorState.schema.nodes.table;
      })(editorState.selection);
      if (!table) {
        return emptyPlaceholder({
          placeholderText: defaultPlaceholderText,
          placeholderPrompts: placeholderPrompts,
          userHadTyped: userHadTyped
        });
      }
      var isFirstCell = (table === null || table === void 0 || (_table$node$firstChil = table.node.firstChild) === null || _table$node$firstChil === void 0 ? void 0 : _table$node$firstChil.content.firstChild) === parentNode;
      if (isFirstCell) {
        return setPlaceHolderState({
          placeholderText: intl.formatMessage(messages.shortEmptyNodePlaceholderText),
          pos: $from.pos,
          placeholderPrompts: placeholderPrompts,
          typedAndDeleted: typedAndDeleted,
          userHadTyped: userHadTyped
        });
      }
    }
    if (nodeTypesWithLongPlaceholderText.includes(parentType) && isEmptyNode) {
      return setPlaceHolderState({
        placeholderText: intl.formatMessage(messages.longEmptyNodePlaceholderText),
        pos: $from.pos,
        placeholderPrompts: placeholderPrompts,
        typedAndDeleted: typedAndDeleted,
        userHadTyped: userHadTyped
      });
    }
    if (nodeTypesWithSyncBlockPlaceholderText.includes(parentType) && isEmptyNode && expValEquals('platform_synced_block', 'isEnabled', true)) {
      return setPlaceHolderState({
        placeholderText: intl.formatMessage(messages.syncBlockPlaceholderText),
        pos: $from.pos,
        placeholderPrompts: placeholderPrompts,
        typedAndDeleted: typedAndDeleted,
        userHadTyped: userHadTyped
      });
    }
    return emptyPlaceholder({
      placeholderText: defaultPlaceholderText,
      placeholderPrompts: placeholderPrompts,
      userHadTyped: userHadTyped
    });
  }
  if (bracketPlaceholderText && bracketTyped(editorState) && isEditorFocused) {
    var _$from = editorState.selection.$from;
    // Space is to account for positioning of the bracket
    var bracketHint = '  ' + bracketPlaceholderText;
    return setPlaceHolderState({
      placeholderText: bracketHint,
      pos: _$from.pos - 1,
      placeholderPrompts: placeholderPrompts,
      typedAndDeleted: typedAndDeleted,
      userHadTyped: userHadTyped
    });
  }
  return emptyPlaceholder({
    placeholderText: defaultPlaceholderText,
    placeholderPrompts: placeholderPrompts,
    userHadTyped: userHadTyped
  });
}
function calculateUserInteractionState(_ref4) {
  var placeholderState = _ref4.placeholderState,
    oldEditorState = _ref4.oldEditorState,
    newEditorState = _ref4.newEditorState;
  var wasEmpty = oldEditorState ? isEmptyDocument(oldEditorState.doc) : true;
  var isEmpty = isEmptyDocument(newEditorState.doc);
  var hasEverTyped = Boolean(placeholderState === null || placeholderState === void 0 ? void 0 : placeholderState.userHadTyped) ||
  // Previously typed
  !wasEmpty ||
  // Had content before
  wasEmpty && !isEmpty; // Just added content
  var justDeletedAll = hasEverTyped && isEmpty && !wasEmpty;
  var isInTypedAndDeletedState = justDeletedAll || Boolean(placeholderState === null || placeholderState === void 0 ? void 0 : placeholderState.typedAndDeleted) && isEmpty;
  // Only reset user interaction tracking when editor is cleanly empty
  var shouldResetInteraction = isEmpty && !isInTypedAndDeletedState;
  return {
    userHadTyped: shouldResetInteraction ? false : hasEverTyped,
    typedAndDeleted: isInTypedAndDeletedState
  };
}
export function createPlugin(intl, defaultPlaceholderText, bracketPlaceholderText, emptyLinePlaceholder, placeholderPrompts, withEmptyParagraph, api) {
  if (!defaultPlaceholderText && !placeholderPrompts && !bracketPlaceholderText) {
    return;
  }
  var isDestroyed = false;
  var activeTypewriterTimeouts = [];
  var clearAllTypewriterTimeouts = function clearAllTypewriterTimeouts() {
    activeTypewriterTimeouts.forEach(function (clearFn) {
      return clearFn();
    });
    activeTypewriterTimeouts = [];
  };
  return new SafePlugin({
    key: pluginKey,
    state: {
      init: function init(_, state) {
        var _api$focus, _api$typeAhead;
        return createPlaceHolderStateFrom({
          isInitial: true,
          isEditorFocused: Boolean(api === null || api === void 0 || (_api$focus = api.focus) === null || _api$focus === void 0 || (_api$focus = _api$focus.sharedState.currentState()) === null || _api$focus === void 0 ? void 0 : _api$focus.hasFocus),
          editorState: state,
          isTypeAheadOpen: api === null || api === void 0 || (_api$typeAhead = api.typeAhead) === null || _api$typeAhead === void 0 ? void 0 : _api$typeAhead.actions.isOpen,
          defaultPlaceholderText: defaultPlaceholderText,
          bracketPlaceholderText: bracketPlaceholderText,
          emptyLinePlaceholder: emptyLinePlaceholder,
          placeholderPrompts: placeholderPrompts,
          typedAndDeleted: false,
          userHadTyped: false,
          intl: intl
        });
      },
      apply: function apply(tr, placeholderState, _oldEditorState, newEditorState) {
        var _api$focus2, _placeholderState$isP, _api$typeAhead2, _ref5, _meta$placeholderText, _ref6, _meta$placeholderProm, _meta$showOnEmptyPara;
        var meta = tr.getMeta(pluginKey);
        var isEditorFocused = Boolean(api === null || api === void 0 || (_api$focus2 = api.focus) === null || _api$focus2 === void 0 || (_api$focus2 = _api$focus2.sharedState.currentState()) === null || _api$focus2 === void 0 ? void 0 : _api$focus2.hasFocus);
        var _calculateUserInterac = calculateUserInteractionState({
            placeholderState: placeholderState,
            oldEditorState: _oldEditorState,
            newEditorState: newEditorState
          }),
          userHadTyped = _calculateUserInterac.userHadTyped,
          typedAndDeleted = _calculateUserInterac.typedAndDeleted;
        var isPlaceholderHidden = (_placeholderState$isP = placeholderState === null || placeholderState === void 0 ? void 0 : placeholderState.isPlaceholderHidden) !== null && _placeholderState$isP !== void 0 ? _placeholderState$isP : false;
        if ((meta === null || meta === void 0 ? void 0 : meta.isPlaceholderHidden) !== undefined && fg('platform_editor_ai_aifc_patch_beta')) {
          isPlaceholderHidden = meta.isPlaceholderHidden;
        }
        if ((meta === null || meta === void 0 ? void 0 : meta.placeholderText) !== undefined && fg('platform_editor_ai_aifc_patch_beta_2')) {
          defaultPlaceholderText = meta.placeholderText;
        }
        var newPlaceholderState = createPlaceHolderStateFrom({
          isEditorFocused: isEditorFocused,
          editorState: newEditorState,
          isTypeAheadOpen: api === null || api === void 0 || (_api$typeAhead2 = api.typeAhead) === null || _api$typeAhead2 === void 0 ? void 0 : _api$typeAhead2.actions.isOpen,
          defaultPlaceholderText: fg('platform_editor_ai_aifc_patch_beta_2') ? defaultPlaceholderText : (_ref5 = (_meta$placeholderText = meta === null || meta === void 0 ? void 0 : meta.placeholderText) !== null && _meta$placeholderText !== void 0 ? _meta$placeholderText : placeholderState === null || placeholderState === void 0 ? void 0 : placeholderState.placeholderText) !== null && _ref5 !== void 0 ? _ref5 : defaultPlaceholderText,
          bracketPlaceholderText: bracketPlaceholderText,
          emptyLinePlaceholder: emptyLinePlaceholder,
          placeholderPrompts: (_ref6 = (_meta$placeholderProm = meta === null || meta === void 0 ? void 0 : meta.placeholderPrompts) !== null && _meta$placeholderProm !== void 0 ? _meta$placeholderProm : placeholderState === null || placeholderState === void 0 ? void 0 : placeholderState.placeholderPrompts) !== null && _ref6 !== void 0 ? _ref6 : placeholderPrompts,
          typedAndDeleted: typedAndDeleted,
          userHadTyped: userHadTyped,
          intl: intl,
          isPlaceholderHidden: isPlaceholderHidden,
          withEmptyParagraph: withEmptyParagraph,
          showOnEmptyParagraph: (_meta$showOnEmptyPara = meta === null || meta === void 0 ? void 0 : meta.showOnEmptyParagraph) !== null && _meta$showOnEmptyPara !== void 0 ? _meta$showOnEmptyPara : placeholderState === null || placeholderState === void 0 ? void 0 : placeholderState.showOnEmptyParagraph
        });

        // Clear timeouts when hasPlaceholder becomes false
        if (!newPlaceholderState.hasPlaceholder) {
          clearAllTypewriterTimeouts();
        }
        return newPlaceholderState;
      }
    },
    props: {
      decorations: function decorations(editorState) {
        var _api$composition, _api$showDiff;
        var _getPlaceholderState = getPlaceholderState(editorState),
          hasPlaceholder = _getPlaceholderState.hasPlaceholder,
          placeholderText = _getPlaceholderState.placeholderText,
          pos = _getPlaceholderState.pos,
          typedAndDeleted = _getPlaceholderState.typedAndDeleted;

        // Decorations is still called after plugin is destroyed
        // So we need to make sure decorations is not called if plugin has been destroyed to prevent the placeholder animations' setTimeouts called infinitely
        if (isDestroyed) {
          return;
        }
        var compositionPluginState = api === null || api === void 0 || (_api$composition = api.composition) === null || _api$composition === void 0 ? void 0 : _api$composition.sharedState.currentState();
        var isShowingDiff = Boolean(api === null || api === void 0 || (_api$showDiff = api.showDiff) === null || _api$showDiff === void 0 || (_api$showDiff = _api$showDiff.sharedState.currentState()) === null || _api$showDiff === void 0 ? void 0 : _api$showDiff.isDisplayingChanges);
        if (hasPlaceholder && ((placeholderText !== null && placeholderText !== void 0 ? placeholderText : '') || placeholderPrompts) && pos !== undefined && !(compositionPluginState !== null && compositionPluginState !== void 0 && compositionPluginState.isComposing) && !isShowingDiff) {
          var initialDelayWhenUserTypedAndDeleted = typedAndDeleted ? TYPEWRITER_TYPED_AND_DELETED_DELAY : 0;
          return createPlaceholderDecoration(editorState, placeholderText !== null && placeholderText !== void 0 ? placeholderText : '', placeholderPrompts, activeTypewriterTimeouts, pos, initialDelayWhenUserTypedAndDeleted);
        }
        return;
      }
    },
    view: function view() {
      var timeoutId;
      function startEmptyParagraphTimeout(editorView) {
        if (timeoutId) {
          return;
        }
        timeoutId = setTimeout(function () {
          timeoutId = undefined;
          editorView.dispatch(editorView.state.tr.setMeta(pluginKey, {
            showOnEmptyParagraph: true
          }));
        }, EMPTY_PARAGRAPH_TIMEOUT_DELAY);
      }
      function destroyEmptyParagraphTimeout() {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = undefined;
        }
      }
      return {
        update: function update(editorView, prevState) {
          if (fg('platform_editor_ai_aifc_patch_beta_2')) {
            var prevPluginState = getPlaceholderState(prevState);
            var newPluginState = getPlaceholderState(editorView.state);

            // user start typing after move to an empty paragraph, clear timeout
            if (!newPluginState.canShowOnEmptyParagraph && timeoutId) {
              destroyEmptyParagraphTimeout();
            }
            // user move to an empty paragraph again, reset state to hide placeholder, and restart timeout
            else if (prevPluginState.canShowOnEmptyParagraph && newPluginState.canShowOnEmptyParagraph && newPluginState.pos !== prevPluginState.pos) {
              editorView.dispatch(editorView.state.tr.setMeta(pluginKey, {
                showOnEmptyParagraph: false
              }));
              destroyEmptyParagraphTimeout();
              startEmptyParagraphTimeout(editorView);
            }
            // user move to an empty paragraph (by click enter or move to an empty paragraph), start timeout
            else if (!prevPluginState.canShowOnEmptyParagraph && newPluginState.canShowOnEmptyParagraph && !newPluginState.showOnEmptyParagraph && !timeoutId) {
              startEmptyParagraphTimeout(editorView);
            }
          }
        },
        destroy: function destroy() {
          clearAllTypewriterTimeouts();
          destroyEmptyParagraphTimeout();
          isDestroyed = true;
        }
      };
    }
  });
}
export var placeholderPlugin = function placeholderPlugin(_ref7) {
  var options = _ref7.config,
    api = _ref7.api;
  var currentPlaceholder = options === null || options === void 0 ? void 0 : options.placeholder;
  return {
    name: 'placeholder',
    commands: {
      setPlaceholder: function setPlaceholder(placeholderText) {
        return function (_ref8) {
          var tr = _ref8.tr;
          if (currentPlaceholder !== placeholderText) {
            currentPlaceholder = placeholderText;
            return tr.setMeta(pluginKey, {
              placeholderText: placeholderText
            });
          }
          return null;
        };
      },
      setAnimatingPlaceholderPrompts: function setAnimatingPlaceholderPrompts(placeholderPrompts) {
        return function (_ref9) {
          var tr = _ref9.tr;
          return tr.setMeta(pluginKey, {
            placeholderPrompts: placeholderPrompts
          });
        };
      },
      setPlaceholderHidden: function setPlaceholderHidden(isPlaceholderHidden) {
        return function (_ref0) {
          var tr = _ref0.tr;
          return tr.setMeta(pluginKey, {
            isPlaceholderHidden: isPlaceholderHidden
          });
        };
      }
    },
    pmPlugins: function pmPlugins() {
      return [{
        name: 'placeholder',
        plugin: function plugin(_ref1) {
          var getIntl = _ref1.getIntl;
          return createPlugin(getIntl(), options && options.placeholder, options && options.placeholderBracketHint, options && options.emptyLinePlaceholder, options && options.placeholderPrompts, options === null || options === void 0 ? void 0 : options.withEmptyParagraph, api);
        }
      }];
    }
  };
};